import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type IconCircleSize = 'sm' | 'md' | 'lg';

export type IconCircleTone = 'yellow' | 'purple' | 'white';

interface IconCircleProps {
  children: ReactNode;
  size?: IconCircleSize;
  tone?: IconCircleTone;
  /** Accessible label — required when the icon conveys meaning on its own */
  label?: string;
  className?: string;
}

const sizes: Record<IconCircleSize, string> = {
  sm: 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4',
  md: 'h-12 w-12 [&>svg]:h-5 [&>svg]:w-5',
  lg: 'h-16 w-16 [&>svg]:h-7 [&>svg]:w-7',
};

const tones: Record<IconCircleTone, string> = {
  yellow: 'bg-brand-yellow text-brand-purple',
  purple: 'bg-brand-purple text-white',
  white: 'bg-white text-brand-purple',
};

/**
 * Filled circle with a centered icon — the yellow variant is the most common
 * accent across the site (Footer IconCircles, ContactInfoBlock labels,
 * Diferenciais carousel arrows).
 */
export function IconCircle({
  children,
  size = 'md',
  tone = 'yellow',
  label,
  className,
}: IconCircleProps) {
  return (
    <span
      role={label ? 'img' : 'presentation'}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(
        'inline-flex items-center justify-center rounded-full shrink-0',
        sizes[size],
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
}
